export type PlaybookConfidence = 'confirmed' | 'partial' | 'unconfirmed';

export type FillState =
  | 'confirmed'
  | 'inferred'
  | 'best_guess'
  | 'not_found'
  | 'conflict'
  | 'user_confirmed';

/** @deprecated use FillState; kept so older packets still parse */
export type ExtractionConfidence = 'high' | 'medium' | 'low';

export type FieldType =
  | 'free_text_short'
  | 'free_text_long'
  | 'single_select'
  | 'multi_select'
  | 'radio_yn'
  | 'number'
  | 'currency'
  | 'reference_lookup'
  | 'action_button'
  | 'checkbox'
  | 'file_attachment';

export type FieldValue = string | string[] | number;

export interface PlaybookOption {
  value: string;
  definition?: string;
  confidence: PlaybookConfidence;
}

export interface FieldCondition {
  fieldId: string;
  equals?: FieldValue;
  includes?: string;
  notEquals?: FieldValue;
  notEqualsAny?: string[];
}

export interface PlaybookField {
  id: string;
  label: string;
  location: string;
  filledBy: string;
  fieldType: FieldType;
  required: boolean | 'conditional';
  options?: PlaybookOption[];
  optionsNote?: string;
  playbookConfidence: PlaybookConfidence;
  showWhen?: FieldCondition;
  clickPath: string[];
  copyGuidance: string;
  warn?: string;
  source: string;
  characterLimit?: number;
}

export interface PlaybookSection {
  id: string;
  title: string;
  intro: string;
  fields: PlaybookField[];
}

export interface AfterSubmitCard {
  id: string;
  stage: string;
  persona: string;
  interfaceName: string;
  summary: string;
  requestorShould: string[];
}

export interface PlaybookWarning {
  id: string;
  severity: 'critical' | 'important' | 'note';
  title: string;
  text: string;
}

export interface NavigationGuide {
  prodUrl: string;
  uatUrl: string;
  steps: string[];
}

export interface GfdPlaybook {
  id: string;
  name: string;
  client: string;
  version: string;
  persona: string;
  interfaceName: string;
  navigation: NavigationGuide;
  warnings: PlaybookWarning[];
  attachments: { id: string; name: string; required: boolean; note: string }[];
  intakeGuidance: IntakeGuidance;
  sections: PlaybookSection[];
  afterSubmit: AfterSubmitCard[];
}

export interface IntakeNeed {
  id: string;
  title: string;
  detail: string;
  unlocks: string;
}

export interface IntakeGuidance {
  headline: string;
  caveat: string;
  minimum: IntakeNeed[];
  better: IntakeNeed[];
  doNotWait: IntakeNeed[];
}

export interface SourceDocument {
  id: string;
  name: string;
  kind: string;
  summary: string;
  excerpt: string;
}

export interface FillCandidate {
  value: FieldValue;
  sourceDocId: string;
  sourceSnippet: string;
  rationale?: string;
  source?: string;
}

export interface SourceCitation {
  docName: string;
  locator: string;
  snippet: string;
}

export interface FieldFill {
  fieldId: string;
  value: FieldValue;
  state?: FillState;
  candidates?: FillCandidate[];
  sourceCitations?: SourceCitation[];
  rationale: string;
  lastUserEditAt?: string | null;
  confirmInServiceNow: boolean;
  flaggedReview?: boolean;
  /** legacy packets */
  extractionConfidence?: ExtractionConfidence;
  sourceDocId?: string;
  sourceSnippet?: string;
}

export interface SeedInitiative {
  id: string;
  name: string;
  client: string;
  champion: { name: string; title: string; note: string };
  consultantNotes: string;
  documents: SourceDocument[];
  fills: FieldFill[];
}

export interface PacketField {
  field: PlaybookField;
  value: FieldValue;
  state: FillState;
  extractionConfidence?: ExtractionConfidence;
  sourceDoc?: SourceDocument;
  sourceSnippet: string;
  sourceCitations: SourceCitation[];
  candidates: FillCandidate[];
  rationale: string;
  confirmInServiceNow: boolean;
  flaggedReview?: boolean;
  visible: boolean;
}

export interface ConfirmedOption {
  fieldId: string;
  value: string;
  confirmedAt: string;
}

export interface GfdDriver {
  id: string;
  gfdFieldId: string;
  name: string;
  unit?: string;
  optimistic: number;
  most_likely: number;
  pessimistic: number;
  source: 'user_stated' | 'extracted' | 'web_benchmark' | 'historical';
  horizon?: 'annual' | 'total';
  track?: string;
  mode?: 'sequential' | 'parallel';
}

export interface GfdManifest {
  drivers: GfdDriver[];
  assumptions: { id?: string; text: string; source?: string }[];
  dependencies: { id?: string; text: string; source?: string }[];
  inflateInternalEstimates?: boolean;
}

export interface ReadinessItem {
  id: string;
  stage: string;
  item: string;
  why: string;
  status: 'ready' | 'not_ready' | 'not_applicable';
  note: string;
}

export interface AccuracyFinding {
  id: string;
  check_id: string;
  fieldId: string | null;
  severity: string;
  status: string;
  message: string;
  proposal?: { action: string; state?: string };
}

export interface ResearchPacket {
  query: string;
  gfdFieldId?: string | null;
  retrievedAt: string;
  content: string;
  sources: { index: number; title: string; url: string; excerpt?: string }[];
}

export const QUEUE_STATES: FillState[] = ['best_guess', 'not_found', 'conflict'];
export const EXPORT_OK_STATES: FillState[] = ['confirmed', 'inferred', 'user_confirmed'];
export const OPTIONAL_FIELD_IDS = new Set(['additional_comments']);
